import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import classNames from 'classnames';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import {
  isValidChannelHandleFormat,
  normalizeChannelHandle,
  validateChannelHandle,
  withChannelHandlePrefix,
  type ChannelHandleValidationError,
} from '@jwp/ott-common/src/utils/channelHandle';
import {
  GET_MY_CHANNEL,
  IS_CHANNEL_HANDLE_AVAILABLE,
  SUGGEST_CHANNEL_HANDLES,
  type GetMyChannelData,
  type IsChannelHandleAvailableData,
  type IsChannelHandleAvailableVariables,
  type SuggestChannelHandlesData,
  type SuggestChannelHandlesVariables,
} from '@jwp/ott-common/src/graphql/query/channel.queries';
import {
  EDIT_CHANNEL,
  type EditChannelData,
  type EditChannelVariables,
} from '@jwp/ott-common/src/graphql/mutation/channel.mutations';
import { GET_S3_UPLOAD_PRESIGNED_URL, type GetS3UploadPresignedUrlData } from '@jwp/ott-common/src/graphql/query/reels.queries';
import { getApolloClient, getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { generateS3Key, type S3UploadType } from '@jwp/ott-common/src/utils/s3';
import { resolveMediaUrl } from '@jwp/ott-common/src/utils/mediaUrl';
import CreateChannelBG from '@jwp/ott-theme/assets/icons/Create_channel_BG.svg';
import UploadIcon from '@jwp/ott-theme/assets/icons/uploadChannel.svg?react';
import EditChannelIcon from '@jwp/ott-theme/assets/icons/Edit_channel.svg?react';
import CheckCircleIcon from '@jwp/ott-theme/assets/icons/check_circle.svg?react';
import { createURL } from '@jwp/ott-common/src/utils/urlFormatting';
import TextField from '../TextField/TextField';
import Button from '../Button/Button';
import Icon from '../Icon/Icon';
import Spinner from '../Spinner/Spinner';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';

import styles from '../CreateChannelModal/CreateChannelModal.module.scss';

const DESCRIPTION_MAX_LENGTH = 300;
const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 30;
const HANDLE_CHECK_DEBOUNCE_MS = 400;
const DEFAULT_CHANNEL_AVATAR = '/static/default-avatar.png';

type HandleStatus = 'idle' | 'checking' | 'available' | 'unavailable' | 'invalid';

type OriginalValues = {
  chId: string;
  chName: string;
  chHandle: string;
  chDescription: string;
  chAvatarUrl: string;
  chBannerUrl: string;
};

const HANDLE_VALIDATION_ERROR_KEYS: Record<Exclude<ChannelHandleValidationError, 'required'>, string> = {
  length: 'create_channel.errors.handle_length',
  charset: 'create_channel.errors.handle_charset',
  start_letter: 'create_channel.errors.handle_start_letter',
  end_special: 'create_channel.errors.handle_end_special',
  consecutive_periods: 'create_channel.errors.handle_consecutive_periods',
  phone_like: 'create_channel.errors.handle_phone_like',
  url_like: 'create_channel.errors.handle_url_like',
};

const EditChannelModal = ({
  setEditChannelPopupConfirmation,
}: {
  setEditChannelPopupConfirmation?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { t } = useTranslation('user');
  const navigate = useNavigate();
  const location = useLocation();
  const { handle: routeHandle = '' } = useParams();
  const user = useAccountStore((state) => state.user);
  const authClient = useMemo(() => getAuthApolloClient(), []);
  const s3Client = useMemo(() => getApolloClient(), []);

  const { data, loading: channelLoading } = useQuery<GetMyChannelData>(GET_MY_CHANNEL, {
    client: authClient,
    fetchPolicy: 'network-only',
  });

  const channel = useMemo(() => {
    const channels = data?.getMyChannel?.channels || [];
    if (!channels.length) {
      return null;
    }
    const normalizedRouteHandle = normalizeChannelHandle(routeHandle);
    const matched = channels.find((item) => normalizeChannelHandle(item.chHandle) === normalizedRouteHandle);
    return matched || channels[0];
  }, [data, routeHandle]);

  const [original, setOriginal] = useState<OriginalValues | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [channelName, setChannelName] = useState('');
  const [handle, setHandle] = useState('');
  const [description, setDescription] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [handleTouched, setHandleTouched] = useState(false);
  const [handleStatus, setHandleStatus] = useState<HandleStatus>('idle');
  const [handleSuggestion, setHandleSuggestion] = useState<string | undefined>();
  const [avatarPreview, setAvatarPreview] = useState(DEFAULT_CHANNEL_AVATAR);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | undefined>();

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);
  const handleCheckIdRef = useRef(0);

  useEffect(() => {
    if (!channel || initialized) {
      return;
    }

    const nextOriginal: OriginalValues = {
      chId: channel.chId,
      chName: channel.chName || '',
      chHandle: normalizeChannelHandle(channel.chHandle),
      chDescription: channel.chDescription || '',
      chAvatarUrl: channel.chAvatarUrl || '',
      chBannerUrl: channel.chBannerUrl || '',
    };

    setOriginal(nextOriginal);
    setChannelName(nextOriginal.chName);
    setHandle(nextOriginal.chHandle);
    setDescription(nextOriginal.chDescription.slice(0, DESCRIPTION_MAX_LENGTH));
    setAvatarPreview(resolveMediaUrl(nextOriginal.chAvatarUrl, DEFAULT_CHANNEL_AVATAR));
    setBannerPreview(nextOriginal.chBannerUrl ? resolveMediaUrl(nextOriginal.chBannerUrl) : null);
    setHandleStatus('available');
    setInitialized(true);
  }, [channel, initialized]);

  const uploadChannelImage = useCallback(
    async (file: File, type: Extract<S3UploadType, 'channel-banner' | 'channel-profile'>): Promise<string> => {
      const key = generateS3Key(user?.id || 'anonymous', file.name, type);
      const { data: uploadData } = await s3Client.query<GetS3UploadPresignedUrlData, { key: string }>({
        query: GET_S3_UPLOAD_PRESIGNED_URL,
        variables: { key },
        fetchPolicy: 'network-only',
      });
      const url = uploadData?.getS3UploadPresignedUrl?.preSignedUrl;
      if (!url) throw new Error('Failed to get upload URL');
      const res = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'image/jpeg' },
      });
      if (!res.ok) throw new Error('Failed to upload image');
      return key;
    },
    [s3Client, user?.id],
  );

  const nameError = useMemo(() => {
    if (!nameTouched) {
      return undefined;
    }
    const trimmedName = channelName.trim();
    if (!trimmedName) {
      return t('create_channel.errors.name_required');
    }
    if (trimmedName.length < NAME_MIN_LENGTH || trimmedName.length > NAME_MAX_LENGTH) {
      return t('create_channel.errors.name_length');
    }
    return undefined;
  }, [channelName, nameTouched, t]);

  const handleFormatError = useMemo(() => {
    if (!handleTouched || !handle) {
      return undefined;
    }
    const validationError = validateChannelHandle(handle);
    if (!validationError || validationError === 'required') {
      return undefined;
    }
    return t(HANDLE_VALIDATION_ERROR_KEYS[validationError]);
  }, [handle, handleTouched, t]);

  const handleRequiredError = handleTouched && !handle ? t('create_channel.errors.handle_required') : undefined;
  const showUnavailableSuggestion = handleStatus === 'unavailable' && Boolean(handleSuggestion);
  const handleFieldError = Boolean(handleRequiredError || handleFormatError || handleStatus === 'unavailable');

  const isHandleUnchanged = Boolean(original && handle.toLowerCase() === original.chHandle.toLowerCase());

  const hasChanges = useMemo(() => {
    if (!original) return false;
    return (
      channelName.trim() !== original.chName.trim() ||
      !isHandleUnchanged ||
      description.trim() !== original.chDescription.trim() ||
      Boolean(avatarFile) ||
      Boolean(bannerFile)
    );
  }, [original, channelName, description, avatarFile, bannerFile, isHandleUnchanged]);

  useEffect(() => {
    setEditChannelPopupConfirmation?.(hasChanges);
  }, [hasChanges, setEditChannelPopupConfirmation]);

  const applyHandleSuggestion = (suggestion: string) => {
    const next = normalizeChannelHandle(suggestion);
    setHandle(next);
    setHandleTouched(true);
  };

  const handleHelperText = (() => {
    if (showUnavailableSuggestion) {
      return (
        <span className={styles.suggestionText}>
          {t('create_channel.errors.handle_taken')}{' '}
          {t('create_channel.errors.handle_unavailable_prefix')}{' '}
          <button
            type="button"
            className={styles.suggestionLink}
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={() => {
              if (!handleSuggestion) {
                return;
              }
              applyHandleSuggestion(handleSuggestion);
            }}
          >
            @{handleSuggestion}
          </button>
        </span>
      );
    }
    if (handleRequiredError) {
      return handleRequiredError;
    }
    if (handleFormatError) {
      return handleFormatError;
    }
    if (handleStatus === 'unavailable') {
      return <span className={styles.suggestionText}>{t('create_channel.errors.handle_taken')}</span>;
    }
    return <span className={styles.handleHelper}>{t('create_channel.handle_helper')}</span>;
  })();

  const canSubmit =
    Boolean(original) &&
    Boolean(channelName.trim()) &&
    channelName.trim().length >= NAME_MIN_LENGTH &&
    channelName.trim().length <= NAME_MAX_LENGTH &&
    Boolean(handle) &&
    isValidChannelHandleFormat(handle) &&
    handleStatus === 'available' &&
    hasChanges &&
    !submitting;

  useEffect(() => {
    return () => {
      if (avatarPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  useEffect(() => {
    return () => {
      if (bannerPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(bannerPreview);
      }
    };
  }, [bannerPreview]);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    if (!handle) {
      // Invalidate any in-flight availability check so it cannot overwrite this state
      handleCheckIdRef.current += 1;
      setHandleStatus('idle');
      setHandleSuggestion(undefined);
      return;
    }

    if (!isValidChannelHandleFormat(handle)) {
      // Invalidate any in-flight availability check so it cannot show "Available" on invalid input
      handleCheckIdRef.current += 1;
      setHandleStatus('invalid');
      setHandleSuggestion(undefined);
      return;
    }

    // Keep current handle as available without availability/suggestion API calls
    if (isHandleUnchanged) {
      handleCheckIdRef.current += 1;
      setHandleStatus('available');
      setHandleSuggestion(undefined);
      return;
    }

    const checkId = ++handleCheckIdRef.current;
    setHandleStatus('checking');
    setHandleSuggestion(undefined);

    const timer = window.setTimeout(async () => {
      const setUnavailableWithSuggestion = (suggestion?: string) => {
        const nextSuggestion = suggestion || `${handle}${Math.floor(1000 + Math.random() * 9000)}`;
        setHandleStatus('unavailable');
        setHandleSuggestion(normalizeChannelHandle(nextSuggestion) || nextSuggestion);
      };

      try {
        const availabilityResult = await authClient.query<IsChannelHandleAvailableData, IsChannelHandleAvailableVariables>({
          query: IS_CHANNEL_HANDLE_AVAILABLE,
          variables: { chHandle: withChannelHandlePrefix(handle) },
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        });

        if (checkId !== handleCheckIdRef.current) {
          return;
        }

        if (typeof availabilityResult.data?.isChannelHandleAvailable !== 'boolean') {
          console.error('Channel handle availability check failed', availabilityResult.error);
          setUnavailableWithSuggestion();
          return;
        }

        if (availabilityResult.data.isChannelHandleAvailable) {
          setHandleStatus('available');
          setHandleSuggestion(undefined);
          return;
        }

        // Only suggest when availability is explicitly false
        let firstSuggestion: string | undefined;
        try {
          let suggestionResult = await authClient.query<SuggestChannelHandlesData, SuggestChannelHandlesVariables>({
            query: SUGGEST_CHANNEL_HANDLES,
            variables: { chHandle: withChannelHandlePrefix(handle) },
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          });

          if (!suggestionResult.data?.suggestChannelHandles) {
            suggestionResult = await authClient.query<SuggestChannelHandlesData, SuggestChannelHandlesVariables>({
              query: SUGGEST_CHANNEL_HANDLES,
              variables: { chHandle: handle },
              fetchPolicy: 'network-only',
              errorPolicy: 'all',
            });
          }

          if (checkId !== handleCheckIdRef.current) {
            return;
          }

          const rawSuggestions = suggestionResult.data?.suggestChannelHandles;
          const suggestions = Array.isArray(rawSuggestions) ? rawSuggestions : rawSuggestions ? [rawSuggestions] : [];

          firstSuggestion = suggestions
            .map((item) => {
              if (item && typeof item === 'object' && 'chHandle' in (item as object)) {
                return normalizeChannelHandle(String((item as { chHandle?: string }).chHandle || ''));
              }
              return normalizeChannelHandle(String(item || ''));
            })
            .find((item) => Boolean(item) && item.toLowerCase() !== handle.toLowerCase());
        } catch (suggestionError) {
          console.error('Channel handle suggestion failed', suggestionError);
        }

        if (checkId !== handleCheckIdRef.current) {
          return;
        }

        setUnavailableWithSuggestion(firstSuggestion);
      } catch (error) {
        if (checkId !== handleCheckIdRef.current) {
          return;
        }
        console.error('Channel handle availability check failed', error);
        setUnavailableWithSuggestion();
      }
    }, HANDLE_CHECK_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [authClient, handle, initialized, isHandleUnchanged]);

  const close = useCallback(
    (nextHandle?: string) => {
      const targetHandle = normalizeChannelHandle(nextHandle || original?.chHandle || routeHandle);
      if (targetHandle && targetHandle !== normalizeChannelHandle(routeHandle)) {
        navigate(`/channel/${targetHandle}`, { replace: true });
        return;
      }
      navigate(createURLFromLocation(location, { u: null }), { replace: true });
    },
    [navigate, location, original?.chHandle, routeHandle],
  );

  const onAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !file.type.startsWith('image/')) {
      return;
    }
    const url = URL.createObjectURL(file);
    setAvatarFile(file);
    setAvatarPreview(url);
  };

  const onBannerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !file.type.startsWith('image/')) {
      return;
    }
    const url = URL.createObjectURL(file);
    setBannerFile(file);
    setBannerPreview(url);
  };

  const onHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = normalizeChannelHandle(event.target.value);
    setHandle(next);
    if (!handleTouched) {
      setHandleTouched(true);
    }
  };

  const onHandleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const caret = input.value.length;
    requestAnimationFrame(() => {
      try {
        input.setSelectionRange(caret, caret);
      } catch {
        // Some input types may not support setSelectionRange
      }
    });
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setNameTouched(true);
    setHandleTouched(true);
    setSubmitError(undefined);

    if (!canSubmit || !original) {
      return;
    }

    setSubmitting(true);
    try {
      const [chAvatarUrl, chBannerUrl] = await Promise.all([
        avatarFile ? uploadChannelImage(avatarFile, 'channel-profile') : Promise.resolve(original.chAvatarUrl),
        bannerFile ? uploadChannelImage(bannerFile, 'channel-banner') : Promise.resolve(original.chBannerUrl),
      ]);

      const nextHandle = normalizeChannelHandle(handle);
      const response = await authClient.mutate<EditChannelData, EditChannelVariables>({
        mutation: EDIT_CHANNEL,
        variables: {
          input: {
            chId: original.chId,
            chName: channelName.trim(),
            ...(isHandleUnchanged ? {} : { chHandle: withChannelHandlePrefix(handle) }),
            chDescription: description.trim(),
            chAvatarUrl,
            chBannerUrl,
          },
        },
        refetchQueries: [{ query: GET_MY_CHANNEL }],
        awaitRefetchQueries: true,
        errorPolicy: 'all',
      });

      const result = response.data?.editChannel;
      const succeeded = !response.errors?.length && result !== false && result !== null && result !== undefined;

      if (!succeeded || response.error) {
        console.error('EditChannel failed', response.error || response.errors);
        setSubmitError(t('edit_channel.errors.update_failed'));
        return;
      }

      useAccountStore.setState({
        isChannelAvailable: true,
        myChannelHandle: nextHandle,
      });

      navigate(createURL(`/channel/${nextHandle}`, { u: 'edit-channel-success' }), {
        replace: true,
        state: { channelHandle: nextHandle },
      });
    } catch (error) {
      console.error('EditChannel failed', error);
      setSubmitError(t('edit_channel.errors.update_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (channelLoading || !initialized) {
    return (
      <div className={styles.container}>
        <LoadingOverlay />
      </div>
    );
  }

  if (!channel || !original) {
    return (
      <div className={styles.container}>
        <div className={styles.formBody}>
          <p className={styles.submitError}>{t('edit_channel.errors.load_failed')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <form onSubmit={onSubmit} noValidate>
        <div className={styles.mediaHeader}>
          <div
            className={classNames(styles.banner, { [styles.bannerImage]: Boolean(bannerPreview) })}
            style={{ backgroundImage: `url(${bannerPreview || CreateChannelBG})` }}
          >
            <button
              type="button"
              className={styles.changeImageButton}
              onClick={() => bannerInputRef.current?.click()}
              aria-label={t('create_channel.change_image')}
            >
              <Icon icon={UploadIcon} />
              <span>{t('create_channel.change_image')}</span>
            </button>
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/*"
              className={styles.hiddenInput}
              onChange={onBannerChange}
            />
          </div>

          <div className={styles.avatarWrap}>
            <img src={avatarPreview} alt="" className={styles.avatar} />
            <button
              type="button"
              className={styles.editAvatarButton}
              onClick={() => avatarInputRef.current?.click()}
              aria-label={t('create_channel.edit_avatar')}
            >
              <Icon icon={EditChannelIcon} className={styles.editAvatarIcon} />
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className={styles.hiddenInput}
              onChange={onAvatarChange}
            />
          </div>
        </div>

        <div className={styles.formBody}>
          <TextField
            className={styles.field}
            name="channelName"
            label={t('create_channel.name_label')}
            placeholder={t('create_channel.name_placeholder')}
            value={channelName}
            onChange={(e) => setChannelName(e.target.value)}
            onBlur={() => setNameTouched(true)}
            required
            maxLength={NAME_MAX_LENGTH}
            error={Boolean(nameError)}
            helperText={nameError}
            showOptionalLabel={false}
          />

          <TextField
            className={classNames(styles.field, styles.handleField)}
            name="channelHandle"
            label={t('create_channel.handle_label')}
            placeholder={''}
            value={handle}
            onChange={onHandleChange}
            onFocus={onHandleFocus}
            onBlur={() => setHandleTouched(true)}
            required
            maxLength={30}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            error={handleFieldError}
            helperText={handleHelperText}
            leftControl={<span className={styles.handlePrefix}>@</span>}
            rightControl={
              handleFormatError ? null : handleStatus === 'checking' ? (
                <span className={styles.handleBadge}>
                  <Spinner size="small" />
                </span>
              ) : handleStatus === 'available' ? (
                <span className={classNames(styles.handleBadge, styles.available)}>
                  <Icon icon={CheckCircleIcon} className={styles.badgeIcon} />
                  {t('create_channel.handle_available')}
                </span>
              ) : handleStatus === 'unavailable' ? (
                <span className={classNames(styles.handleBadge, styles.unavailable)}>
                  <span className={styles.statusDot} aria-hidden="true">
                    !
                  </span>
                  {t('create_channel.handle_not_available')}
                </span>
              ) : null
            }
            showOptionalLabel={false}
          />

          <TextField
            className={styles.field}
            name="channelDescription"
            label={
              <span className={styles.descriptionLabel}>
                {t('create_channel.description_label')}
                <span className={styles.counter}>
                  {description.length} / {DESCRIPTION_MAX_LENGTH}
                </span>
              </span>
            }
            placeholder={t('create_channel.description_placeholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX_LENGTH))}
            multiline
            maxLength={DESCRIPTION_MAX_LENGTH}
            showOptionalLabel={false}
          />

          <div className={styles.footer}>
            {submitError ? <p className={styles.submitError}>{submitError}</p> : null}
            <Button
              type="submit"
              label={t('edit_channel.save_button')}
              variant="contained"
              color="primary"
              disabled={!canSubmit}
              busy={submitting}
              className={styles.createButton}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditChannelModal;